import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Box,
    CircularProgress,
    Stack,
    Typography,
    Rating,
    Paper,
    Grid,
    MenuItem,
    Select,
    FormControl
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import FlagIcon from '@mui/icons-material/Flag';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import { get, del } from '../../../components/hooks/apiFetch';
import { PromptSelectionModal } from './PromptSelectionModal'; // Импортируем модальное окно
import './AdvancedPromptInput.css';

const STORAGE_KEYS = {
    PROMPT_SOURCE_UNIVERSAL: 'prompt_source_universal',
    PROMPT_TEXT_UNIVERSAL: 'prompt_text_universal',
    PROMPT_INPUT_UNIVERSAL: 'prompt_input_universal',
    PROMPT_RATING_UNIVERSAL: 'prompt_rating_universal',
    PROMPT_STATUS_UNIVERSAL: 'prompt_status_universal',
    PROMPT_MODEL_UNIVERSAL: 'prompt_model_universal',
    PROMPT_VERSION_UNIVERSAL: 'prompt_version_universal',
};

const TEXT_CHARACTER_LIMIT = 6000;

// Маппинг статусов из enum
const STATUS_MAP = {
    'Standard': { label: 'Стандартный', color: 'default' },
    'Test': { label: 'Тестовый', color: 'warning' },
    'Rough': { label: 'Черновой', color: 'error' },
    'Production': { label: 'Продакшен', color: 'success' }
};

const MODEL_MAP = {
    'None': { label: 'None', color: 'default' },
    'FourB': { label: '4B', color: 'default' },
    'ThirtyB': { label: '30B', color: 'default' },
    'FourteenB': { label: '14B', color: 'default' }
};

// Компонент для рейтинга с кастомным стилем
const CustomRating = ({ value, onChange, readOnly = false, size = 'medium' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Rating
            className="rating"
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            precision={0.5}
            size={size}
            sx={{
                color: 'var(--DT1520-Black) !important',
                '& .MuiRating-iconFilled': {
                    color: '#ff9800',
                },
                '& .MuiRating-iconHover': {
                    color: '#ffb74d',
                },
                borderColor: 'var(--DT1520-Black) !important',
            }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray)' }}>
            {value?.toFixed(1) || '0.0'}
        </Typography>
    </Box>
);

export const AdvancedPromptInput = ({
    isLoading,
    onSourceChange,
    onTextChange,
    onSave,
    selectedSource,
    textValue,
}) => {
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [ratingValue, setRatingValue] = useState(0);
    const [statusValue, setStatusValue] = useState('');
    const [modelValue, setModelValue] = useState('');
    const [versionValue, setVersionValue] = useState([]);
    const [versions, setVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [descriptionValue, setDescriptionValue] = useState('');
    const [metaValue, setMetaValue] = useState('');
    const [isRestoring, setIsRestoring] = useState(true);
    const [modalOpen, setModalOpen] = useState(false); // Состояние для модального окна
    const isRestoringRef = useRef(true);

    const fetchOptions = async () => {
        setLoading(true);
        try {
            const data = await get('/api/Prompt');
            await restoreSavedState(data);
        } catch (error) {
            console.error('Error fetching options:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedSource) {
            if (typeof selectedSource === 'object') {
                setInputValue(selectedSource.name || '');
                setRatingValue(selectedSource.rating || 0);
                setStatusValue(selectedSource.status || 0);
                setModelValue(selectedSource.model || 0);
                setDescriptionValue(selectedSource.description || '');

            } else {
                setInputValue(selectedSource);
            }
        }
    }, [selectedSource]);

    const abortControllerRef = useRef(null);

    const fetchVersions = async (IsSaved = false) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        if (selectedSource && typeof selectedSource === 'object' && selectedSource.guid) {
            setLoadingVersions(true);
            try {
                const data = await get(
                    `/api/Prompt/promptversions?promptGuid=${encodeURIComponent(selectedSource.guid)}`,
                    {
                        signal: abortControllerRef.current.signal
                    });

                if (abortControllerRef.current.signal.aborted) return;

                setVersions(data);

                const savedVersion = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL));
                if (savedVersion && !IsSaved) {
                    const currentVersion = data.find(x => x.promptGuid === savedVersion.promptGuid && x.version === savedVersion.version);
                    updateDataForVersion(currentVersion);
                } else if (data.length > 0) {
                    const latestVersion = data.find(x => x.version === Math.max(...data.map(v => v.version)));
                    updateDataForVersion(latestVersion);
                    sessionStorage.setItem(STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL, JSON.stringify(latestVersion));
                }
            } catch (error) {
                console.error('Ошибка при загрузке версий:', error);
            } finally {
                if (!abortControllerRef.current?.signal.aborted) {
                    setLoadingVersions(false);
                }
            }
        } else {
            setVersions([]);
            setVersionValue(0);
        }
    };

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        fetchOptions();
    }, []);

    useEffect(() => {
        fetchVersions(true);
    }, [selectedSource?.guid]);

    const restoreSavedState = async (optionsData) => {
        setIsRestoring(true);
        isRestoringRef.current = true;
        try {
            const savedSource = sessionStorage.getItem(STORAGE_KEYS.PROMPT_SOURCE_UNIVERSAL);
            const savedText = sessionStorage.getItem(STORAGE_KEYS.PROMPT_TEXT_UNIVERSAL);
            const savedInput = sessionStorage.getItem(STORAGE_KEYS.PROMPT_INPUT_UNIVERSAL);
            const savedRating = sessionStorage.getItem(STORAGE_KEYS.PROMPT_RATING_UNIVERSAL);
            const savedStatus = sessionStorage.getItem(STORAGE_KEYS.PROMPT_STATUS_UNIVERSAL);
            const savedModel = sessionStorage.getItem(STORAGE_KEYS.PROMPT_MODEL_UNIVERSAL);
            const savedVersion = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL));

            if (savedInput) setInputValue(savedInput);
            if (savedText) onTextChange(savedText);

            if (savedRating !== null && savedRating !== undefined) {
                setRatingValue(parseFloat(savedRating));
            }

            if (savedStatus !== null && savedStatus !== undefined) {
                setStatusValue(savedStatus);
            }

            if (savedModel !== null && savedModel !== undefined) {
                setModelValue(savedModel);
            }

            if (savedSource) {
                const sourceData = JSON.parse(savedSource);

                if (sourceData?.guid) {
                    const foundSource = optionsData.find(opt => opt.guid === sourceData.guid);
                    if (foundSource) {
                        onSourceChange(foundSource);
                        setDescriptionValue(foundSource.description ?? '');
                        if (!savedText && foundSource.prompt) {
                            onTextChange(foundSource.prompt);
                        }

                        if (savedRating === null || savedRating === undefined) {
                            setRatingValue(foundSource.rating || 0);
                        }
                        if (savedStatus === null || savedStatus === undefined) {
                            setStatusValue(foundSource.status || 0);
                        }
                        if (savedModel === null || savedModel === undefined) {
                            setModelValue(foundSource.model || 0);
                        }
                        if (savedVersion !== null) {
                            updateDataForVersion(savedVersion);
                        }
                    }
                } else if (typeof sourceData === 'string') {
                    onSourceChange(sourceData);
                }
            }

        } catch (error) {
            console.error('Ошибка в восстановлении состояния', error);
        } finally {
            setTimeout(() => {
                setIsRestoring(false);
                isRestoringRef.current = false;
            }, 100);
        }
    };

    const handleModalSelect = (selected) => {
        if (selected === '') {
            // Создание нового промпта
            onSourceChange('');
            setInputValue('');
            onTextChange('');
            setRatingValue(0);
            setStatusValue(0);
            setModelValue(0);
            setDescriptionValue('');
        } else if (selected) {
            // Выбор существующего промпта
            onSourceChange(selected);
            setInputValue(selected.name || '');
            onTextChange(selected.prompt || '');
            setDescriptionValue(selected.description ?? '');
            setRatingValue(selected.rating || 0);
            setStatusValue(selected.status || 0);
            setModelValue(selected.model || 0);
        }
    };

    const saveRatingAndStatusToStorage = () => {
        if (isRestoringRef.current) {
            return;
        }

        sessionStorage.setItem(STORAGE_KEYS.PROMPT_RATING_UNIVERSAL, ratingValue.toString());
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_STATUS_UNIVERSAL, statusValue.toString());
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_MODEL_UNIVERSAL, modelValue.toString());
    };

    const handleRatingChange = (event, newValue) => {
        if (isRestoringRef.current) {
            return;
        }

        setRatingValue(newValue);
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_RATING_UNIVERSAL, newValue.toString());
    };

    const handleStatusChange = (event) => {
        if (isRestoringRef.current) {
            return;
        }

        const newStatus = event.target.value;
        setStatusValue(newStatus);
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_STATUS_UNIVERSAL, newStatus.toString());

        if (selectedSource && typeof selectedSource === 'object') {
            onSourceChange({
                ...selectedSource,
                status: newStatus
            });
        }
    };

    const handleVersionChange = (event) => {
        if (isRestoringRef.current) {
            return;
        }

        const newVersion = event.target.value;
        updateDataForVersion(newVersion);
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL, JSON.stringify(newVersion));
    };

    const updateDataForVersion = (version) => {
        if (!version || !selectedSource) return;
        setVersionValue(version);
        onTextChange(version.prompt || '');

        const updatedSource = {
            ...selectedSource,
            version: version.version,
            updateAt: version.creationAt || selectedSource.updateAt,
            lastRedactorLogin: version.creatorLogin || selectedSource.lastRedactorLogin,
        };

        onSourceChange(updatedSource);
    };

    const handleModelChange = (event) => {
        if (isRestoringRef.current) {
            return;
        }

        const newModel = event.target.value;
        setModelValue(newModel);
        sessionStorage.setItem(STORAGE_KEYS.PROMPT_MODEL_UNIVERSAL, newModel.toString());

        if (selectedSource && typeof selectedSource === 'object') {
            onSourceChange({
                ...selectedSource,
                model: newModel
            });
        }
    };

    useEffect(() => {
        if (isRestoring) {
            return;
        }
        saveRatingAndStatusToStorage();
    }, [ratingValue, statusValue, modelValue, versionValue, isRestoring]);

    const handleSave = async () => {
        if (isRestoringRef.current) {
            return;
        }

        const sourceWithData = selectedSource && typeof selectedSource === 'object'
            ? { ...selectedSource, rating: ratingValue, status: statusValue, model: modelValue }
            : inputValue;

        await onSave({ ...sourceWithData, version: versionValue.version }, textValue, ratingValue, statusValue, modelValue, descriptionValue);

        if (selectedSource && textValue) {
            if (typeof selectedSource === 'object') {
                sessionStorage.setItem(STORAGE_KEYS.PROMPT_SOURCE_UNIVERSAL, JSON.stringify({
                    guid: selectedSource.guid,
                    name: selectedSource.name
                }));
            }
            sessionStorage.setItem(STORAGE_KEYS.PROMPT_TEXT_UNIVERSAL, textValue);
        }
        await fetchOptions();
        fetchVersions(true);
    };

    const handleDeleteVersion = async () => {
        try {
            const isConfirmed = window.confirm('Вы уверены, что хотите удалить версию промпта?');
            if (!isConfirmed) {
                return;
            }
            await del(`/api/Prompt/promptversion?promptGuid=${encodeURIComponent(selectedSource.guid)}&version=${encodeURIComponent(versionValue.version)}`);
            fetchVersions(true);
        }
        catch (error) {
            console.error('Ошибка при удалении версии промпта:', error);
        }
    };

    const handleDownloadPrompt = () => {
        if (isRestoringRef.current) return;

        try {
            const fileName = (inputValue || selectedSource?.name || 'prompt').replace(/[<>:"/\\|?*]/g, '_');
            const fileContent = textValue || '';

            const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Ошибка экспорта:', error);
            alert('Не удалось сохранить промпт в файл');
        }
    };

    const handleCopyPrompt = () => {
        if (!selectedSource?.guid) {
            return
        }
        onSourceChange(selectedSource.name);
        setInputValue(selectedSource.name);
        onTextChange(textValue);
        setDescriptionValue(selectedSource.description);
    };

    const handleUploadPrompt = () => {
        if (isRestoringRef.current) return;

        if (textValue?.trim() || inputValue?.trim()) {
            if (!window.confirm('Текущий промпт будет заменён содержимым файла. Продолжить?')) {
                return;
            }
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt,text/plain';

        input.onchange = async (e) => {
            try {
                const file = e.target.files?.[0];
                if (!file) return;

                const content = await file.text();

                const fileName = file.name.replace(/\.[^/.]+$/, '').replace(/[<>:"/\\|?*]/g, '_');

                setRatingValue(0);
                setStatusValue(0);
                setModelValue(0);

                onSourceChange(fileName);
                setInputValue(fileName);
                onTextChange(content.trim());
                setDescriptionValue(`Импортирован из файла: ${file.name}`);

                sessionStorage.setItem(STORAGE_KEYS.PROMPT_INPUT_UNIVERSAL, fileName);
                sessionStorage.setItem(STORAGE_KEYS.PROMPT_TEXT_UNIVERSAL, content.trim());
                sessionStorage.setItem(STORAGE_KEYS.PROMPT_RATING_UNIVERSAL, '0');
                sessionStorage.setItem(STORAGE_KEYS.PROMPT_STATUS_UNIVERSAL, '0');
                sessionStorage.setItem(STORAGE_KEYS.PROMPT_MODEL_UNIVERSAL, '0');
                sessionStorage.setItem(
                    STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL,
                    JSON.stringify({ version: 1 })
                );

                alert(`Промпт "${fileName}" успешно загружен!`);
            } catch (error) {
                console.error('Ошибка импорта:', error);
                alert(`Не удалось загрузить промпт: ${error.message}`);
            } finally {
                input.value = '';
            }
        };

        input.click();
    };

    const handleDeleteSelected = async () => {
        if (isRestoringRef.current) {
            return;
        }

        if (!selectedSource || !selectedSource.guid) {
            console.error('Не выбран промпт для удаления');
            return;
        }

        const isConfirmed = window.confirm('Вы уверены, что хотите удалить этот промпт?');
        if (!isConfirmed) {
            return;
        }

        setDeletingId(selectedSource.guid);
        try {
            await del(`/api/Prompt?guid=${encodeURIComponent(selectedSource.guid)}`);
            onSourceChange(null);
            onTextChange('');
            setInputValue('');
            setRatingValue(0);
            setStatusValue(0);
            setModelValue(0);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_RATING_UNIVERSAL);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_STATUS_UNIVERSAL);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_MODEL_UNIVERSAL);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_VERSION_UNIVERSAL);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_SOURCE_UNIVERSAL);
            sessionStorage.removeItem(STORAGE_KEYS.PROMPT_TEXT_UNIVERSAL);
        } catch (error) {
            console.error('Ошибка при удалении промпта:', error);
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (isRestoring) {
            return;
        }

        if (selectedSource) {
            const sourceToSave = typeof selectedSource === 'object'
                ? { guid: selectedSource.guid, name: selectedSource.name }
                : selectedSource;
            sessionStorage.setItem(STORAGE_KEYS.PROMPT_SOURCE_UNIVERSAL, JSON.stringify(sourceToSave));
        }
    }, [selectedSource, isRestoring]);

    useEffect(() => {
        if (isRestoring) {
            return;
        }

        sessionStorage.setItem(STORAGE_KEYS.PROMPT_TEXT_UNIVERSAL, textValue);
    }, [textValue, isRestoring]);

    useEffect(() => {
        if (isRestoring) {
            return;
        }

        if (inputValue) sessionStorage.setItem(STORAGE_KEYS.PROMPT_INPUT_UNIVERSAL, inputValue);
    }, [inputValue, isRestoring]);

    useEffect(() => {
        console.log(statusValue);
        setMetaValue(`# NAME: ${inputValue}
# AUTHOR: ${selectedSource?.creatorLogin ?? ''}
# EDITOR: ${selectedSource?.lastRedactorLogin ?? ''}
# STATUS: ${STATUS_MAP[statusValue]?.label ?? 'Неизвестно'}
# MODEL: ${MODEL_MAP[modelValue]?.label ?? 'Неизвестно'}
# VERSION: ${versionValue?.version ?? ''}
# CREATIONAT ${selectedSource?.creationAt ?? ''}
# UPDATEAT ${selectedSource?.updateAt ?? ''}
# RATING: ${ratingValue}
# COMMENT: ${descriptionValue}`);
    }, [inputValue, selectedSource, statusValue, ratingValue, descriptionValue, versionValue, modelValue]);

    const canSave = !isLoading && (textValue || selectedSource?.guid) && !isRestoringRef.current;
    const canDelete = selectedSource?.guid && !isLoading && !isRestoringRef.current;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack spacing={1.5} sx={{ maxWidth: '100%', margin: 'auto' }}>
            {/* Модальное окно выбора промпта */}
            <PromptSelectionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSelect={handleModalSelect}
                selectedSource={selectedSource}
            />

            <Typography
                fontWeight="4px"
                sx={{ color: 'var(--DT1520-Red) !important' }}
                fontSize="20px"
            >
                {selectedSource?.id ? `Настройка промпта ID ${selectedSource?.id}` : 'Создание AI промпта'}
            </Typography>

            {/* Заменяем Autocomplete на кнопку выбора */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    fullWidth
                    label="Выберите промпт"
                    variant="outlined"
                    value={inputValue || (selectedSource ? (typeof selectedSource === 'object' ? selectedSource.name || '' : selectedSource) : '')}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        if (selectedSource) {
                            onSourceChange(e.target.value);
                        }
                    }}
                    InputProps={{
                        endAdornment: loading && <CircularProgress color="primary" size={20} />
                    }}
                    placeholder="Введите название нового промпта или выберите существующий"
                />
                <Button
                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                    onClick={() => setModalOpen(true)}
                    startIcon={<SearchIcon />}
                    sx={{
                        minWidth: '120px',
                        height: '56px'
                    }}
                >
                    Выбрать
                </Button>
            </Box>

            {/* Единая панель рейтинга и статуса */}
            <Paper
                className="rating-and-status"
                elevation={1}
                sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}
            >
                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                    {/* Метаинформация промпта */}
                    <Grid size={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <TextField
                            disabled
                            label="Метаинформация промпта"
                            variant="outlined"
                            value={metaValue}
                            fullWidth
                            multiline
                            sx={{
                                flexGrow: 1,
                                '& .MuiInputBase-root': {
                                    height: '100%',
                                    alignItems: 'flex-start'
                                },
                                '& .MuiInputBase-inputMultiline': {
                                    height: '100% !important',
                                    minHeight: '200px'
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={8} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Stack spacing={2} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Stack
                                direction="row"
                                useFlexGap
                                spacing={2}
                                sx={{ flexWrap: "wrap", flexShrink: 0 }}
                            >
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                        <FlagIcon sx={{ fontSize: 14, mr: 0.5, color: '#3f51b5' }} />
                                        Модели
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            className="standard-select"
                                            value={modelValue}
                                            onChange={handleModelChange}
                                        >
                                            {Object.entries(MODEL_MAP).map(([key, model]) => (
                                                <MenuItem key={key} value={key}>
                                                    {model.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                        <FlagIcon sx={{ fontSize: 14, mr: 0.5, color: '#3f51b5' }} />
                                        Статус
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            className="standard-select"
                                            value={statusValue}
                                            onChange={handleStatusChange}
                                        >
                                            {Object.entries(STATUS_MAP).map(([key, status]) => (
                                                <MenuItem key={key} value={key}>
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {selectedSource && typeof selectedSource === 'object' && (
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                            <FlagIcon sx={{ fontSize: 14, mr: 0.5, color: '#3f51b5' }} />
                                            Версия
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                className="standard-select"
                                                value={versionValue}
                                                onChange={handleVersionChange}
                                                disabled={loadingVersions || versions.length === 0}
                                            >
                                                {loadingVersions ? (
                                                    <MenuItem value="" disabled>Загрузка...</MenuItem>
                                                ) : versions.length > 0 ? (
                                                    versions
                                                        .slice()
                                                        .sort((a, b) => {
                                                            if (b.version !== a.version) {
                                                                return b.version - a.version;
                                                            }
                                                            if (!a.creationAt && !b.creationAt) return 0;
                                                            if (!a.creationAt) return 1;
                                                            if (!b.creationAt) return -1;

                                                            return new Date(b.creationAt).getTime() - new Date(a.creationAt).getTime();
                                                        }).map((version) => (
                                                            <MenuItem key={version.guid} value={version}>
                                                                Версия {version.version}
                                                                {version.creationAt ? ` (${new Date(version.creationAt).toLocaleDateString()})` : ''}
                                                            </MenuItem>
                                                        ))
                                                ) : (
                                                    <MenuItem value={0} disabled>Нет версий</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                        <StarIcon sx={{ fontSize: 14, mr: 0.5, color: '#ff9800' }} />
                                        Рейтинг
                                    </Typography>
                                    <CustomRating value={ratingValue} onChange={handleRatingChange} />
                                </Box>

                                {selectedSource && typeof selectedSource === 'object' && (
                                    <>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                                <PersonIcon sx={{ fontSize: 14, mr: 0.5, color: '#4caf50' }} />
                                                Создатель
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {selectedSource.creatorLogin || 'Не указан'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important', mb: 0.5, display: 'block' }}>
                                                <EditIcon sx={{ fontSize: 14, mr: 0.5, color: '#2196f3' }} />
                                                Редактор
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {selectedSource.lastRedactorLogin || selectedSource.creatorLogin || 'Не указан'}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                            <TextField
                                label="Комментарий промпта"
                                variant="outlined"
                                value={descriptionValue}
                                onChange={(e) => setDescriptionValue(e.target.value)}
                                fullWidth
                                multiline
                                sx={{
                                    flexGrow: 1,
                                    '& .MuiInputBase-root': {
                                        height: '100%',
                                        alignItems: 'flex-start'
                                    },
                                    '& .MuiInputBase-inputMultiline': {
                                        height: '100% !important',
                                        minHeight: '100px'
                                    }
                                }}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Текст промпта */}
            <TextField
                label="Текст промпта"
                variant="outlined"
                value={textValue}
                onChange={(e) => {
                    if (isRestoringRef.current) {
                        return;
                    }
                    e.target.value.length <= TEXT_CHARACTER_LIMIT && onTextChange(e.target.value);
                }}
                fullWidth
                multiline
                rows={10}
                helperText={`${textValue.length} / ${TEXT_CHARACTER_LIMIT} символов`}
                FormHelperTextProps={{
                    sx: {
                        textAlign: 'right',
                        color: textValue.length > TEXT_CHARACTER_LIMIT ? 'red' : 'inherit',
                        marginTop: 0.5,
                    }
                }}
            />

            {/* Кнопки действий */}
            <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadPrompt}
                        sx={{ py: 1.5 }}
                        size="large"
                        disabled={isRestoring || !(textValue || selectedSource?.guid)}
                    >
                        Экспорт
                    </Button>
                </Grid>
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        startIcon={<FileUploadIcon />}
                        onClick={handleUploadPrompt}
                        sx={{ py: 1.5 }}
                        size="large"
                        disabled={isRestoring}
                    >
                        Импорт
                    </Button>
                </Grid>
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyPrompt}
                        sx={{ py: 1.5 }}
                        size="large"
                        disabled={isRestoring}
                    >
                        Клонировать
                    </Button>
                </Grid>
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={!canSave}
                        size="large"
                        sx={{ py: 1.5 }}
                    >
                        {isLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Grid>
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button red-button"
                        startIcon={deletingId === selectedSource?.guid ? <CircularProgress size={20} /> : <DeleteIcon />}
                        onClick={handleDeleteSelected}
                        disabled={!canDelete}
                        size="large"
                        sx={{ py: 1.5 }}
                    >
                        {deletingId === selectedSource?.guid ? 'Удаление...' : 'Удалить'}
                    </Button>
                </Grid>
                <Grid item xs="auto" sm={6} md={4} lg={2}>
                    <Button
                        fullWidth
                        className="dt1520-button active-dt1520-button view-button red-button"
                        startIcon={deletingId === selectedSource?.guid ? <CircularProgress size={20} /> : <DeleteIcon />}
                        onClick={handleDeleteVersion}
                        disabled={versions.length < 2 || selectedSource?.version === 1}
                        size="large"
                        sx={{ py: 1.5 }}
                    >
                        {deletingId === selectedSource?.guid ? 'Удаление...' : 'Удалить версию'}
                    </Button>
                </Grid>
            </Grid>
        </Stack>
    );
};